import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { pendoSignOut } from "@newsela/snitch";
import { useRouter } from "next/router";
import PropTypes from "prop-types";

import evaluateRestQuery from "@/remoteState/evaluateRestQuery";
import useLazyRestQuery from "@/remoteState/useLazyRestQuery";
import useRestMutation from "@/remoteState/useRestMutation";
import authenticatedServerSideRequestConfig from "@/utils/authenticatedServerSideRequestConfig";
import { API_BASE_URL } from "@/api/api.config";

import isRunningOnClient from "@/utils/isRunningOnClient";
import getQueryClient, {
  SESSION_STORAGE_OFFLINE_CACHE_KEY,
} from "@/utils/getQueryClient/getQueryClient";

const UserQueryContext = createContext({ data: null });
const UserMethodsContext = createContext({});
const UserCEContext = createContext();

const propTypes = {
  userQueryInitialValue: PropTypes.shape({
    data: PropTypes.object,
    loading: PropTypes.bool,
    error: PropTypes.instanceOf(Error),
    isReducedShape: PropTypes.bool,
  }),
  children: PropTypes.node,
};

const getMeUrl = `${API_BASE_URL}/api/v2/user/me/`;

export const UserProvider = ({
  userQueryInitialValue = {
    data: undefined,
    loading: true,
    error: undefined,
  },
  children,
}) => {
  // check for early access
  const router = useRouter();
  let hasCE;

  if (
    router.query && router.query.newsela_ce && router.query.newsela_ce ? 1 : 0
  ) {
    hasCE = router.query.newsela_ce ? 1 : 0;
  }

  const signOutRequest = useMemo(() => {
    return new Request(`${API_BASE_URL}/api/v2/session/`, {
      method: "DELETE",
    });
  }, []);
  const [signOut] = useRestMutation(signOutRequest);

  const [getMe] = useLazyRestQuery(getMeUrl);

  // isReducedShape is a hydration decision, not user data, so keep it in a ref
  // instead of the published context. A reduced SSR payload omits licenses and
  // schools, so the mount effect must still refetch to backfill them.
  const { isReducedShape, ...initialUserData } = userQueryInitialValue;
  const isReducedShapeRef = useRef(isReducedShape);

  const [userData, setUserData] = useState({
    ...initialUserData,
    loading: true,
  });

  const signOutAndRefreshMe = useCallback(async () => {
    await signOut();
    pendoSignOut();
    setUserData({ ...userData, loading: true });

    // Clear the query cache from 🏝️ TansTack Query as well
    const queryClient = getQueryClient();
    queryClient.clear();
    queryClient.removeQueries({ queryKey: ["user"] });
    if (isRunningOnClient()) {
      window.sessionStorage.removeItem(SESSION_STORAGE_OFFLINE_CACHE_KEY);
    }

    return await getMe().then((responseUserData) => {
      setUserData({ ...responseUserData, loading: false });
    });
  }, [signOut, getMe]);

  const refreshMe = useCallback(
    async (getReducedShape = false) => {
      const reducedUserRequest =
        getMeUrl + "?include_licenses=false&include_schools=false";
      setUserData({ ...userData, loading: true });
      const responseUserData = await (getReducedShape
        ? evaluateRestQuery(reducedUserRequest)
        : getMe());
      setUserData({ ...responseUserData, loading: false });
      return responseUserData;
    },
    [getMe],
  );

  const isFirstEffectRun = useRef(true);

  useEffect(() => {
    // On the first client render, if SSR already provided a full authenticated
    // user, skip the redundant /v2/user/me refetch and just clear loading.
    // A ref that is checked and flipped inside the effect is immune to effect
    // ordering. useIsMounted was the wrong tool here: it reports "mounted", not
    // "first render", so its guard was already stale by the time this effect
    // ran. Later runs, such as a changed getMe, still refetch.
    // A reduced SSR shape omits licenses and schools, so it must not be reused.
    // The client refetch is what backfills them, and skipping it would strand
    // the whole session on the reduced user across client-side navigation.
    const isFirstRun = isFirstEffectRun.current;
    isFirstEffectRun.current = false;
    if (
      isFirstRun &&
      userData.data?.is_authenticated &&
      !isReducedShapeRef.current
    ) {
      setUserData((prev) => ({ ...prev, loading: false }));
      return;
    }
    getMe().then((responseUserData) => {
      setUserData({ ...responseUserData, loading: false });
    });
  }, [getMe]);

  const methods = useMemo(
    () => ({ signOut: signOutAndRefreshMe, refreshMe }),
    [signOutAndRefreshMe, refreshMe],
  );

  return (
    <UserCEContext.Provider value={hasCE}>
      <UserQueryContext.Provider value={userData}>
        <UserMethodsContext.Provider value={methods}>
          {children}
        </UserMethodsContext.Provider>
      </UserQueryContext.Provider>
    </UserCEContext.Provider>
  );
};

UserProvider.propTypes = propTypes;

UserProvider.__getInitialProps = async ({ ctx, getReducedUserShape }) => {
  const userMeQueryParam = new URLSearchParams();
  if (getReducedUserShape) {
    userMeQueryParam.append("include_licenses", "false");
    userMeQueryParam.append("include_schools", "false");
  }
  const request = new Request(
    getMeUrl + `?${userMeQueryParam.toString()}`,
    authenticatedServerSideRequestConfig(ctx),
  );
  const userQueryInitialValue = await evaluateRestQuery(request);
  return {
    userQueryInitialValue: {
      ...userQueryInitialValue,
      isReducedShape: !!getReducedUserShape,
    },
  };
};

const useUser = () => {
  return useContext(UserQueryContext);
};

/**
 * @deprecated Use V3 instead
 */
export const useSignOut = () => {
  return useContext(UserMethodsContext).signOut;
};

/**
 * @deprecated Use V3 instead
 */
export const useRefreshMe = () => {
  return useContext(UserMethodsContext).refreshMe;
};

export { UserCEContext };
export default useUser;
